import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  NgZone,
  output,
  signal,
} from '@angular/core';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { PermissionsService } from '@frontendlib/common/features/permissions/services/permissions.service';
import { formatCountBadge } from '@modules/common/utils/format-count-badge';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { ViewSubjects } from '@sharedlib/common/permissions/types/Permissions';
import { Subject } from 'rxjs';
import { HUB_TABS, isCompactHubWidth, type HubTab } from '../hub-tabs';

/** Coalesces ResizeObserver bursts — a split-view drag fires one per animation frame. */
const RESIZE_DEBOUNCE_MS = 100;

interface HubTabView extends HubTab {
  readonly count: number;
  readonly countLabel: string;
  readonly isActive: boolean;
}

@Component({
  selector: 'app-entity-hub-tabs',
  standalone: true,
  templateUrl: './entity-hub-tabs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, PopoverDirective],
  host: {
    class: 'block',
  },
})
export class EntityHubTabsComponent {
  public readonly active = input.required<StitchType>();
  public readonly counts = input<Partial<Record<StitchType, number>>>({});

  public readonly typeChange = output<StitchType>();

  protected readonly compact = signal(false);
  protected readonly menuOpen = signal(false);
  protected readonly menuClose = new Subject<void>();

  protected readonly items = computed<HubTabView[]>(() => {
    const counts = this.counts();
    const active = this.active();

    return HUB_TABS.filter(
      (tab) =>
        tab.type !== StitchType.SHEET || this.permissionsService.checkPermission('read', ViewSubjects.sheetsView),
    ).map((tab) => {
      const count = counts[tab.type] ?? 0;

      return { ...tab, count, countLabel: formatCountBadge(count), isActive: tab.type === active };
    });
  });

  protected readonly activeItem = computed<HubTabView>(
    () => this.items().find((tab) => tab.isActive) ?? this.items()[0],
  );

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly permissionsService = inject(PermissionsService);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    afterNextRender(() => this.observeWidth());
  }

  protected handleSelect(type: StitchType): void {
    if (type !== this.active()) {
      this.typeChange.emit(type);
    }
  }

  /** The popover only closes itself on an outside click, so a menu pick has to say so. */
  protected handleMenuSelect(type: StitchType): void {
    this.menuClose.next();
    this.handleSelect(type);
  }

  private observeWidth(): void {
    const element = this.host.nativeElement;
    let debounce: ReturnType<typeof setTimeout> | undefined;

    const measure = (): void => {
      const compact = isCompactHubWidth(Math.round(element.getBoundingClientRect().width));
      if (compact === this.compact()) {
        return;
      }

      // Enter the zone only on a real change: a continuous drag must not cost a
      // change-detection cycle per frame.
      this.ngZone.run(() => this.compact.set(compact));
    };

    measure();

    this.ngZone.runOutsideAngular(() => {
      const observer = new ResizeObserver(() => {
        clearTimeout(debounce);
        debounce = setTimeout(measure, RESIZE_DEBOUNCE_MS);
      });
      observer.observe(element);

      this.destroyRef.onDestroy(() => {
        clearTimeout(debounce);
        observer.disconnect();
      });
    });
  }
}
